/**
 * Labels for the Overview's every-vertical Ticket Pipeline and Equipment
 * Tracker (api#1136). Exhaustive per vertical; an unknown status reads "—",
 * never its raw key and never a guessed name.
 *
 * Words are the ones each vertical's own screen already uses, so a row here
 * reads the same as the list it leads to:
 *   trucking statuses   lib/load-status LOAD_STATUS_BADGE
 *   downhole tickets    pages/downhole/ticketBits DH_TICKET_STATUS_SKIN
 *   downhole tools      lib/downhole-labels DH_COMPONENT_STATUS_SKIN
 *   instruments         lib/pressure-testing PT_STATUS_SKIN (calibration)
 *   rental tickets      RentalFieldTickets' stat strip
 *   PT tickets          pressure-testing FieldTicketsScreen's chips
 *   NDT tickets         lib/ndt-tickets NDT_TICKET_STATUS_SKIN
 *   fleet units         TrucksScreen's stat strip
 *   rental assets       AssetRegistry's STATUS map
 */
import { LOAD_STATUS_BADGE } from './load-status';
import { DH_COMPONENT_STATUS_SKIN } from './downhole-labels';
import { PT_STATUS_SKIN } from './pressure-testing';
import { PF_TICKET_STATUS_SKIN } from './pf-tickets';
import { NDT_TICKET_STATUS_SKIN } from './ndt-tickets';
import { WT_TICKET_STATUS_SKIN } from './wt-tickets';
import { WS_TICKET_STATUS_SKIN } from './ws-tickets';
import { DH_TICKET_STATUS_SKIN } from '../pages/downhole/ticketBits';
import type { MoneyVertical } from '../types/overview-money';
import type { EquipmentVertical, PipelineStage } from '../types/overview-ops';

const labels = (m: Record<string, { label: string }>): Record<string, string> =>
  Object.fromEntries(Object.entries(m).map(([k, v]) => [k, v.label]));

/** Casing's rows are drawn by the casing pipeline component, with its own labels. */
const PIPELINE_STATUS_LABEL: Record<Exclude<MoneyVertical, 'casing_running'>, Record<string, string>> = {
  trucking: labels(LOAD_STATUS_BADGE),
  downhole_tools: labels(DH_TICKET_STATUS_SKIN),
  surface_rentals: {
    draft: 'Draft', pending: 'Out for signature', signed: 'Signed', approved: 'Approved', invoiced: 'Invoiced', rejected: 'Rejected',
  },
  pressure_testing: { draft: 'Draft', signed: 'Signed', approved: 'Approved', invoiced: 'Invoiced', void: 'Void' },
  ndt_inspection: labels(NDT_TICKET_STATUS_SKIN),
  // PF-6: the ticket screen's words (lib/pf-tickets), plus the API's derived
  // 'invoiced' (a customer-approved ticket on a live invoice).
  pipeline_facility: { ...labels(PF_TICKET_STATUS_SKIN), invoiced: 'Invoiced' },
  // WT-6: the ticket screen's words (lib/wt-tickets), plus the API's derived 'invoiced'.
  well_testing: { ...labels(WT_TICKET_STATUS_SKIN), invoiced: 'Invoiced' },
  // WS-6: the ticket screen's words (lib/ws-tickets), plus the API's derived 'invoiced'.
  well_servicing: { ...labels(WS_TICKET_STATUS_SKIN), invoiced: 'Invoiced' },
};

export function pipelineStatusLabel(vertical: string, status: string): string {
  return (PIPELINE_STATUS_LABEL as Record<string, Record<string, string>>)[vertical]?.[status] ?? '—';
}

/**
 * The bar's fill, by the API's `stage`: the five signal hues grouped by what
 * the row asks of the office. Unknown stage, slate.
 */
export const STAGE_FILL: Record<PipelineStage, string> = {
  in_progress: 'bg-fi-signal-slate',
  awaiting_approval: 'bg-fi-signal-amber',
  approved: 'bg-fi-signal-green',
  invoiced: 'bg-fi-signal-green',
  rejected: 'bg-fi-signal-red',
  closed: 'bg-fi-signal-slate',
};

const EQUIPMENT_STATUS_LABEL: Record<EquipmentVertical, Record<string, string>> = {
  trucking: { active: 'Active', maintenance: 'Maintenance', out_of_service: 'Out of service' },
  surface_rentals: { available: 'Available', rented: 'On Rent', reserved: 'Reserved', maintenance: 'Maintenance' },
  downhole_tools: labels(DH_COMPONENT_STATUS_SKIN),
  pressure_testing: labels(PT_STATUS_SKIN),
};

export function equipmentStatusLabel(vertical: string, status: string): string {
  return (EQUIPMENT_STATUS_LABEL as Record<string, Record<string, string>>)[vertical]?.[status] ?? '—';
}

/** A vertical's block title when the API could not name it (a failed adapter sends its raw key). */
const PIPELINE_TITLE: Record<MoneyVertical, string> = {
  casing_running: 'Field tickets', trucking: 'Bills of lading', surface_rentals: 'Rental tickets',
  downhole_tools: 'Downhole tickets', pressure_testing: 'Pressure-test tickets', ndt_inspection: 'NDT tickets',
  pipeline_facility: 'Pipeline tickets', well_testing: 'Well-test tickets', well_servicing: 'Well-servicing tickets',
};
const EQUIPMENT_TITLE: Record<EquipmentVertical, string> = {
  trucking: 'Fleet', surface_rentals: 'Rental assets', downhole_tools: 'Downhole tools', pressure_testing: 'Instruments',
};
export const pipelineTitle = (v: string) => (PIPELINE_TITLE as Record<string, string>)[v] ?? 'Another vertical';
export const equipmentTitle = (v: string) => (EQUIPMENT_TITLE as Record<string, string>)[v] ?? 'Another vertical';
